import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  FlagIcon
} from '@heroicons/react/24/outline';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${props => props.theme.zIndex.modal};
  padding: ${props => props.theme.spacing.md};
`;

const Modal = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.typography.h4.fontSize};
  font-weight: ${props => props.theme.typography.h4.fontWeight};
  color: ${props => props.theme.colors.text.primary};
  
  svg {
    width: 24px;
    height: 24px;
    color: ${props => props.theme.colors.warning};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.text.secondary};
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.hover};
    color: ${props => props.theme.colors.text.primary};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Content = styled.div`
  padding: ${props => props.theme.spacing.lg};
`;

const ShowInfo = styled.div`
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ShowTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ShowDetails = styled.div`
  font-size: ${props => props.theme.typography.caption.fontSize};
  color: ${props => props.theme.colors.text.secondary};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  font-weight: 500;
  color: ${props => props.theme.colors.text.primary};
`;

const ReasonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${props => props.theme.spacing.sm};
`;

const ReasonOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.colors.hover};
  }
  
  &:has(input:checked) {
    background: ${props => props.theme.colors.accent}20;
    border-color: ${props => props.theme.colors.accent};
  }
`;

const RadioInput = styled.input`
  margin: 0;
`;

const ReasonText = styled.div`
  flex: 1;
  
  .title {
    font-weight: 500;
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: ${props => props.theme.spacing.xs};
  }
  
  .description {
    font-size: ${props => props.theme.typography.caption.fontSize};
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const TextArea = styled.textarea`
  background: ${props => props.theme.colors.input.background};
  border: 1px solid ${props => props.theme.colors.input.border};
  color: ${props => props.theme.colors.text.primary};
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.body.fontSize};
  font-family: inherit;
  resize: vertical;
  min-height: 80px;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.text.muted};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  justify-content: flex-end;
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const Button = styled.button`
  background: ${props => props.$primary ? props.theme.colors.gradient : 'transparent'};
  border: 1px solid ${props => props.$primary ? props.theme.colors.accent : props.theme.colors.border};
  color: ${props => props.$primary ? 'white' : props.theme.colors.text.primary};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  font-size: ${props => props.theme.typography.body.fontSize};
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$primary ? props.theme.colors.gradientHover : props.theme.colors.hover};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const reportReasons = [
  {
    id: 'spam',
    title: 'Spam or Fake Event',
    description: 'Promotional spam, fake shows, or duplicate posts'
  },
  {
    id: 'inappropriate',
    title: 'Inappropriate Content',
    description: 'Offensive language, discriminatory content, or NSFW material'
  },
  {
    id: 'wrong-info',
    title: 'Wrong Information',
    description: 'Incorrect venue, date, time, or band details'
  },
  {
    id: 'scam',
    title: 'Scam or Fraud',
    description: 'Suspicious ticketing, fake venues, or fraudulent activity'
  },
  {
    id: 'harassment',
    title: 'Harassment',
    description: 'Targeting specific individuals or groups'
  },
  {
    id: 'other',
    title: 'Other',
    description: 'Something else that violates community guidelines'
  }
];

const ReportModal = ({ show, isOpen, onClose, onSubmit }) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedReason) return;

    setIsSubmitting(true);
    try {
      // Ensure message meets database constraint (minimum 10 characters)
      let message = details.trim();
      if (message.length < 10) {
        // If details are too short, use the reason description as the message
        const selectedReasonData = reportReasons.find(r => r.id === selectedReason);
        message = selectedReasonData ? selectedReasonData.description : 'Report submitted for review';
      }

      await onSubmit({
        showId: show.id,
        reason: selectedReason,
        details: message,
        reportedAt: new Date()
      });
      
      // Reset form
      setSelectedReason('');
      setDetails('');
      onClose();
    } catch (error) {
      console.error('Error submitting report:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedReason('');
    setDetails('');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Overlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <Modal
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Header>
              <Title>
                <FlagIcon />
                Report Show
              </Title>
              <CloseButton onClick={handleClose}>
                <XMarkIcon />
              </CloseButton>
            </Header>

            <Content>
              <ShowInfo>
                <ShowTitle>{show.band_name}</ShowTitle>
                <ShowDetails>
                  {show.venue_name} • {show.formattedDate} • {show.genre}
                </ShowDetails>
              </ShowInfo>

              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>Why are you reporting this show?</Label>
                  <ReasonGrid>
                    {reportReasons.map((reason) => (
                      <ReasonOption key={reason.id}>
                        <RadioInput
                          type="radio"
                          name="reason"
                          value={reason.id}
                          checked={selectedReason === reason.id}
                          onChange={(e) => setSelectedReason(e.target.value)}
                        />
                        <ReasonText>
                          <div className="title">{reason.title}</div>
                          <div className="description">{reason.description}</div>
                        </ReasonText>
                      </ReasonOption>
                    ))}
                  </ReasonGrid>
                </FormGroup>

                <FormGroup>
                  <Label>Additional Details (Optional)</Label>
                  <TextArea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder="Provide any additional context that would help moderators review this report..."
                    maxLength={500}
                  />
                </FormGroup>

                <ButtonGroup>
                  <Button type="button" onClick={handleClose}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    $primary 
                    disabled={!selectedReason || isSubmitting}
                  >
                    {isSubmitting ? 'Reporting...' : 'Submit Report'}
                  </Button>
                </ButtonGroup>
              </Form>
            </Content>
          </Modal>
        </Overlay>
      )}
    </AnimatePresence>
  );
};

export default ReportModal; 